"use client";

import { ExternalLink, X } from "lucide-react";
import Link from "next/link";
import type { CSSProperties } from "react";
import { AffiliateImpression } from "@/components/affiliate/affiliate-impression";
import { AffiliateLink } from "@/components/affiliate/affiliate-link";
import { offerGoSlug } from "@/lib/affiliate/go-link";
import type { AffiliatePlatform } from "@/lib/affiliate/links";
import type { Locale } from "@/lib/i18n/config";
import { accentOnCard, accentPair } from "@/lib/monetization/accent";
import { decisionTraceProps } from "@/lib/monetization/decision-trace";
import type { NormalizedOffer } from "@/lib/monetization/providers";
import { RemoveAdsLink } from "./remove-ads-link";
import { SponsoredLabel } from "./sponsored-label";

/**
 * The one card every monetized unit renders through.
 *
 * Split out of `native-ad.tsx` so the two entry points share a single
 * template: `<NativeAd>` (client, lazy, engine-ranked) and `<PinnedOffer>`
 * (server, deterministic, for a contractual placement). Before this existed
 * there were five near-identical single-CTA cards — `PaymentPlatformCard`,
 * `EducativeCard`, `DeelHiringCta`, `blog/educative-cta` and this one — and
 * only this one carried the sponsored label, the dismiss control, the
 * disclosure and the reserved height.
 *
 * Colour comes through `@/lib/monetization/accent`, never raw: `offer.accent`
 * is free text (a `PROVIDER_LIST` literal, or an operator-editable
 * `affiliate_offers.accent` cell) and painting it straight into a background
 * under a hardcoded `text-white` shipped three AA failures. See that module's
 * header for the measured ratios.
 */
export function OfferCard({
	offer,
	market,
	surface,
	jobId,
	locale,
	className,
	anchor,
	positionIndex,
	onDismiss,
	testId,
	onImpression,
	canFire,
	verbatimLink = false,
}: {
	offer: NormalizedOffer;
	/**
	 * Render the partner's tracking URL itself instead of the go-link. Only for
	 * a contract that pins the exact URL in the served HTML (the Deel challenge
	 * post, `PinnedOffer`) — everywhere else a raw tracking URL books crawler
	 * clicks at the partner.
	 */
	verbatimLink?: boolean;
	market?: string;
	surface: string;
	jobId: string | null;
	locale: Locale;
	className?: string;
	anchor?: string;
	positionIndex?: number;
	onDismiss?: () => void;
	/** Forwarded to the observed wrapper — see `AffiliateImpression.testId`. */
	testId?: string;
	/** Forwarded to the observed wrapper — see `AffiliateImpression.onImpression`. */
	onImpression?: () => void;
	/** Forwarded to the observed wrapper — see `AffiliateImpression.canFire`. */
	canFire?: () => boolean;
}) {
	const l = locale === "en" ? "en" : "id";
	// Contrast-safe derivations of the provider's brand colour: `pill` is the
	// CTA's background plus the black/white label that reads on it, `eyebrow`
	// is the accent shaded per theme until it clears AA on `bg-card`.
	const pill = accentPair(offer.accent);
	// Every outbound link here opens in a new tab (AffiliateLink sets
	// target="_blank"). The arrow glyph announces that visually and is
	// aria-hidden, so without this a screen-reader user gets no warning that
	// focus is about to leave the page.
	const newTabHint =
		locale === "en" ? "(opens in a new tab)" : "(terbuka di tab baru)";
	const eyebrowColor = accentOnCard(offer.accent);
	const platform = offer.provider as AffiliatePlatform | "educative";
	// One primary offer per card: the whole card is the link (stretched-link
	// pattern), so there is exactly one anchor and one `affiliate_click`.
	const primary = offer.items?.[0];
	const primaryHref = primary?.href ?? offer.href;
	const primaryTitle = primary?.title ?? offer.title;
	// Every engine offer rides the go-route (see `offerGoSlug`): the route
	// redirects to `href ?? items[0].href`, the same link this card renders.
	const goSlug = offerGoSlug(offer, verbatimLink);
	// Placement telemetry (PR-5) — shared by the impression beacon and every
	// outbound click below, so exposure(anchor) → apply_intent_clicked can be
	// funneled per anchor/position in PostHog without new events.
	const placementExtra = {
		anchor,
		position_index: positionIndex,
	};

	return (
		<AffiliateImpression
			platform={offer.provider}
			surface={surface}
			locale={l}
			jobId={jobId}
			extra={{
				market,
				decision_reason: offer.decision?.reason,
				eligible_arms: offer.decision?.eligible,
				...decisionTraceProps(offer.decision),
				...placementExtra,
			}}
			className={className}
			testId={testId}
			onImpression={onImpression}
			canFire={canFire}
		>
			<div
				data-offer-item={primary?.id ?? "single"}
				className="relative overflow-hidden rounded-xl border border-border/60 bg-card"
			>
				{/* Accent strip — 4px of the partner's raw brand colour. DESIGN.md's
				    One Voice Rule reserves chroma for actionable elements and bans a
				    second accent; this is the one knowing exception, because a
				    sponsored unit has to read as the partner's and not as ours. It is
				    decorative and non-text, so no contrast ratio applies, and it is
				    the only place the unshaded value appears. */}
				<div className="h-1 w-full" style={{ backgroundColor: offer.accent }} />
				<div className="p-5">
					<div className="mb-2 flex items-center justify-between gap-2">
						{/* Both colour classes are literal so Tailwind's JIT emits
						    them; the per-theme values ride in on CSS vars. A `dark:`
						    override holding a baked hex would be the exact workaround
						    src/components/CLAUDE.md forbids. */}
						<p
							className="text-[color:var(--offer-eyebrow-l)] text-xs font-semibold uppercase tracking-[0.12em] dark:text-[color:var(--offer-eyebrow-d)]"
							style={
								{
									"--offer-eyebrow-l": eyebrowColor.light,
									"--offer-eyebrow-d": eyebrowColor.dark,
								} as CSSProperties
							}
						>
							{offer.eyebrow}
						</p>
						<div className="flex items-center gap-1.5">
							<SponsoredLabel
								locale={l}
								advertiseHref={`/${l}/advertise?src=${surface}`}
							/>
							{onDismiss ? (
								<button
									type="button"
									onClick={onDismiss}
									aria-label={l === "id" ? "Sembunyikan iklan" : "Dismiss ad"}
									// -m-1.5 keeps the glyph optically where it was while the
									// hit area grows to 26px. `text-muted-foreground/50` halved
									// a token that is already 56% alpha, landing near 28%
									// effective — under the 3:1 floor for a UI control, on the
									// mid-tier Android screens most of this audience uses.
									className="relative z-10 -m-1.5 rounded-full p-1.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-reduce:transition-none"
								>
									<X className="h-3.5 w-3.5" aria-hidden="true" />
								</button>
							) : null}
						</div>
					</div>

					{offer.image ? (
						// Fixed aspect ratio, not intrinsic sizing: the card's whole CLS
						// story is the reserved min-height, and an image laid out at its
						// natural size would push the copy down the moment it decodes.
						// biome-ignore lint/performance/noImgElement: next/image would route an already-optimized, 24h-cached PNG from the og-renderer worker through the image optimizer — an extra hop on the request path for no gain, and it would need a remotePatterns entry per creative host.
						<img
							src={offer.image}
							alt=""
							loading="lazy"
							decoding="async"
							className="mb-3 aspect-[1200/630] w-full rounded-lg object-cover"
						/>
					) : null}

					<p className="line-clamp-2 text-sm font-semibold text-foreground">
						{primaryTitle}
					</p>
					<p className="mt-1 line-clamp-3 text-xs leading-relaxed text-muted-foreground">
						{offer.desc}
					</p>

					{primaryHref ? (
						<AffiliateLink
							platform={platform}
							href={primaryHref}
							goSlug={goSlug}
							surface={surface}
							jobId={jobId}
							locale={l}
							extra={{
								...decisionTraceProps(offer.decision),
								...placementExtra,
							}}
							// Hover and press shade the FILL (see `accentPair`), never the
							// label: `hover:opacity-90` — the idiom this replaces — fades a
							// 12px semibold label that already sits near the AA floor.
							// `active:` matters more than `hover:` here: most of this
							// audience is on a touch screen, where hover never fires and
							// this was previously the only feedback the control had.
							className="mt-4 inline-flex min-h-9 after:absolute after:inset-0 after:content-[''] items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold transition-[background-color] hover:[background-color:var(--offer-cta-hover)] active:[background-color:var(--offer-cta-hover)] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-reduce:transition-none motion-reduce:active:scale-100"
							style={
								{
									backgroundColor: pill.bg,
									color: pill.fg,
									"--offer-cta-hover": pill.bgHover,
								} as CSSProperties
							}
						>
							{offer.cta || (l === "id" ? "Lihat" : "View")}
							<span className="sr-only">{newTabHint}</span>
							<ExternalLink className="h-3 w-3" aria-hidden="true" />
						</AffiliateLink>
					) : null}

					<div className="mt-3 flex items-center justify-between gap-2">
						<Link
							href={`/${l}/affiliate-disclosure`}
							className="relative z-10 text-xs text-muted-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
						>
							{l === "id" ? "Tautan afiliasi" : "Affiliate link"}
						</Link>
						{/* House upsell: "Remove ads — Pro →", secondary to the affiliate
							    CTA above (so it never cannibalizes affiliate revenue), shown to
							    the free/anon viewer who is already looking at an ad — the
							    highest-intent moment for the remove-ads pitch, and the only
							    exit once `adsDismissible` is false. RemoveAdsLink documents the
							    events it fires. */}
						<RemoveAdsLink
							locale={l}
							surface={surface}
							provider={offer.provider}
						/>
					</div>
				</div>
			</div>
		</AffiliateImpression>
	);
}
